import { useState } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { ScrollArea, ScrollBar } from '../ui/scroll-area';
import { Menu, X, ChevronDown } from 'lucide-react';

/**
 * MobileTabNav - A mobile-optimized tab navigation
 * Shows horizontal scroll on desktop, bottom sheet grid on mobile
 */
export function MobileTabNav({ 
  tabs, 
  activeTab, 
  onTabChange, 
  className 
}) {
  const [isOpen, setIsOpen] = useState(false);
  
  const activeTabData = tabs.find(t => t.id === activeTab);
  
  const handleSelect = (tabId, onClick) => {
    onTabChange(tabId);
    if (onClick) onClick();
    setIsOpen(false);
  };

  return (
    <div className={cn("mb-4", className)}>
      {/* Mobile: Dropdown trigger + Bottom Sheet */}
      <div className="md:hidden">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="outline" 
              className="w-full justify-between h-12 bg-white border-slate-200"
            >
              <div className="flex items-center gap-2">
                {activeTabData?.icon && <activeTabData.icon className="w-4 h-4 text-teal-600" />}
                <span className="font-medium">{activeTabData?.label || 'Select'}</span>
                {activeTabData?.badge && (
                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                    {activeTabData.badge}
                  </Badge>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl px-4">
            <div className="flex items-center justify-between py-3 border-b mb-4">
              <h3 className="font-semibold text-lg">Navigation</h3>
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <ScrollArea className="h-[calc(70vh-80px)]">
              <div className="grid grid-cols-3 gap-3 pb-6">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  if (tab.hidden) return null;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleSelect(tab.id, tab.onClick)}
                      data-testid={`mobile-tab-${tab.id}`}
                      className={cn(
                        "flex flex-col items-center gap-2 p-3 rounded-xl text-center transition-all relative",
                        isActive 
                          ? "bg-teal-50 border-2 border-teal-400" 
                          : "bg-slate-50 border-2 border-transparent hover:border-slate-200"
                      )}
                    >
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center",
                        isActive ? "bg-teal-100" : "bg-white"
                      )}>
                        {Icon && <Icon className={cn("w-5 h-5", isActive ? "text-teal-600" : "text-slate-500")} />}
                      </div>
                      <span className={cn(
                        "text-[11px] font-medium leading-tight",
                        isActive ? "text-teal-700" : "text-slate-600"
                      )}>
                        {tab.label}
                      </span>
                      {tab.badge && (
                        <Badge className="absolute -top-1 -right-1 text-[9px] px-1.5 py-0 bg-amber-500 text-white">
                          {tab.badge}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop: Horizontal scrollable tabs */}
      <div className="hidden md:block">
        <ScrollArea className="w-full">
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-max min-w-full">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              if (tab.hidden) return null;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleSelect(tab.id, tab.onClick)}
                  data-testid={`tab-${tab.id}`}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all whitespace-nowrap",
                    isActive 
                      ? "bg-white text-teal-700 shadow-sm" 
                      : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                  )}
                >
                  {Icon && <Icon className={cn("w-4 h-4", isActive ? "text-teal-600" : "text-slate-400")} />}
                  {tab.label}
                  {tab.badge && (
                    <Badge className={cn(
                      "ml-1 text-[10px] px-1.5 py-0",
                      isActive ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"
                    )}>
                      {tab.badge}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </div>
  );
}

/**
 * QuickActionBar - Bottom fixed bar for most common actions (mobile only)
 */
export function QuickActionBar({ actions, className }) {
  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-slate-200 px-4 py-2 safe-area-bottom z-50",
      className
    )}>
      <div className="flex items-center justify-around">
        {actions.map((action, idx) => {
          const Icon = action.icon;
          return (
            <button
              key={idx}
              onClick={action.onClick}
              className={cn(
                "flex flex-col items-center gap-1 p-2 rounded-lg transition-colors",
                action.active ? "text-teal-600" : "text-slate-500"
              )}
            >
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center",
                action.active ? "bg-teal-100" : "bg-slate-100"
              )}>
                {Icon && <Icon className="w-5 h-5" />}
              </div>
              <span className="text-[10px] font-medium">{action.label}</span>
              {action.badge && (
                <Badge className="absolute -top-1 -right-1 text-[9px] px-1 bg-red-500 text-white">
                  {action.badge}
                </Badge>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
